<template>
  <!-- Component value is === {{ componentChangedValue }} -->
  <div v-if="!loading">
    <component :is="currentComponent" />
  </div>
</template>

<script setup>
import { computed, onMounted, watch, ref } from "vue";
import HotelSearchPage from "./ListingPage/HotelSearchPage.vue";
import HotelDetails from "../../views/hotelDetails/details.vue";
import R2BDetails from "../../views/hotelDetails/R2BDetails.vue";
import CheckOut from "../../views/booking/checkOut.vue";
import confirmationBooking from "../../views/booking/confirmationBooking.vue";
import { useComponentChangeStore } from "../../stores/componentChange.module";
import { useSearchStore } from "../../stores/search.module";

const componentChangeStore = useComponentChangeStore();
const searchStore = useSearchStore();

// Map store values to components
const componentMap = {
  mainView: HotelSearchPage,
  hotelView: HotelDetails,
  r2bView: R2BDetails,
  bookingView: CheckOut,
  BookingConfirmationView: confirmationBooking,
};

const initializeComponent = () => {
  // Check URL parameters FIRST before localStorage
  const urlParams = new URLSearchParams(document.location.search);
  const viewParam = urlParams.get("view");
  
  // If URL has view parameter, use it (this handles r2bView)
  if (viewParam && componentMap[viewParam]) {
    componentChangeStore.changeTheComponent(viewParam);
    return;
  }
  
  const savedComponent = localStorage.getItem("componentLoad");
  const propertyDetailsPayload = JSON.parse(localStorage.getItem("propertyDetailsPayload"));

  // Prioritize the saved component from localStorage
  if (savedComponent && componentMap[savedComponent]) {
    componentChangeStore.changeTheComponent(savedComponent);
    return;
  }

  // If no saved component, check for property details
  if (propertyDetailsPayload) {
    componentChangeStore.changeTheComponent("hotelView");
    return;
  }

  // Default to main view
  componentChangeStore.changeTheComponent("mainView");
};

initializeComponent();

// Computed property to dynamically select the component
const currentComponent = computed(() => {
  return componentMap[componentChangeStore.getComponentName] || HotelSearchPage;
});

// Computed property to display the current component name
const componentChangedValue = computed(
  () => componentChangeStore.getComponentName
);

const loading = ref(true);

onMounted(() => {
  let params = new URLSearchParams(document.location.search);
  let propertyName = params.get("propertyName");
  let propertyId = params.get("propertyId");
  let viewParam = params.get("view"); // Parameter to control which view to display
  let tabId = params.get("tabId"); // Parameter to identify the tab
  
  // Determine if this is a tab with URL parameters
  const isTabWithParams = tabId && viewParam && componentMap[viewParam];
  
  // Get current tab ID from localStorage or URL
  const currentTabId = tabId || localStorage.getItem("currentTabId") || "main_tab";
  
  // Store tab ID for this browser tab/session if not already set
  if (!localStorage.getItem("currentTabId")) {
    localStorage.setItem("currentTabId", currentTabId);
  }
  
  try {
    // CASE 1: This is a tab with URL parameters (details or booking view)
    if (isTabWithParams) {
      // Set the component based on URL 'view' parameter
      componentChangeStore.changeTheComponent(viewParam);
      
      // Store tab-specific component preference
      localStorage.setItem(`componentLoad_${tabId}`, viewParam);
      
      // Check if we need to load hotel details (for both hotel, r2b and booking views)
      if ((viewParam === "hotelView" || viewParam === "r2bView" || viewParam === "bookingView") && propertyName && propertyId) {
        // Set up dates similar to getResetData function
        const today = new Date();
        today.setDate(today.getDate() + 14);
        const checkinDateValue = today.toISOString().split("T")[0];

        const checkout = new Date(today);
        checkout.setDate(checkout.getDate() + 2);
        const checkoutDateValue = checkout.toISOString().split("T")[0];

        // Store hotel details in localStorage for this tab
        const propertyDetailsPayload = {
          slugifiedPropertyName: propertyName,
          propertyId: propertyId,
          checkin: checkinDateValue,
          checkout: checkoutDateValue,
          rooms: [{ adults: 2, children: 0, ages: [] }],
        };
        localStorage.setItem(
          "propertyDetailsPayload",
          JSON.stringify(propertyDetailsPayload)
        );

        // Update search store with payload details
        searchStore.setHotelRoomId(propertyId);
        searchStore.setHotelRoomName(propertyName);
        searchStore.setCheckin(checkinDateValue);
        searchStore.setCheckout(checkoutDateValue);
        searchStore.setRooms([{ adults: 2, children: 0, ages: [] }]);
      }
      
      // If specifically on booking view, check for booking data in localStorage
      if (viewParam === "bookingView") {
        // Load any saved booking data if available
        const bookingData = localStorage.getItem("bookingData");
        if (bookingData) {
          // No need to reprocess it here as the booking component will handle it
          console.log("Booking data found and will be used by booking component");
        }
      }
    } 
    // CASE 2: If no view param but we have property parameters
    else if (propertyName && propertyId) {
      // Set up dates similar to getResetData function
      const today = new Date();
      today.setDate(today.getDate() + 14);
      const checkinDateValue = today.toISOString().split("T")[0];

      const checkout = new Date(today);
      checkout.setDate(checkout.getDate() + 2);
      const checkoutDateValue = checkout.toISOString().split("T")[0];

      // Store hotel details in localStorage
      const propertyDetailsPayload = {
        slugifiedPropertyName: propertyName,
        propertyId: propertyId,
        checkin: checkinDateValue,
        checkout: checkoutDateValue,
        rooms: [{ adults: 2, children: 0, ages: [] }],
      };
      localStorage.setItem(
        "propertyDetailsPayload",
        JSON.stringify(propertyDetailsPayload)
      );

      // Update search store with payload details
      searchStore.setHotelRoomId(propertyId);
      searchStore.setHotelRoomName(propertyName);
      searchStore.setCheckin(checkinDateValue);
      searchStore.setCheckout(checkoutDateValue);
      searchStore.setRooms([{ adults: 2, children: 0, ages: [] }]);

      componentChangeStore.changeTheComponent("hotelView");
    } else {
      // CASE 3: This is likely the original tab or a direct page load
      
      // Check for tab-specific saved component in localStorage first
      const tabSpecificComponent = localStorage.getItem(`componentLoad_${currentTabId}`);
      
      // Then fall back to the general component 
      const savedComponent = tabSpecificComponent || localStorage.getItem("componentLoad");
      
      if (savedComponent && componentMap[savedComponent]) {
        // If a valid component is saved, use it
        componentChangeStore.changeTheComponent(savedComponent);
      } else {
        // Check if propertyDetailsPayload exists in localStorage
        const existingPayload = localStorage.getItem("propertyDetailsPayload");
        
        if (existingPayload) {
          // If payload exists, navigate to hotel view
          componentChangeStore.changeTheComponent("hotelView");
        } else {
          // If no parameters and no payload, explicitly set to main view
          componentChangeStore.changeTheComponent("mainView");
        }
      }
    }
  } catch (error) {
    console.error("Error in component initialization:", error);
    // Fallback to main view in case of any error
    componentChangeStore.changeTheComponent("mainView");
  } finally {
    loading.value = false;
  }
});

watch(
  () => componentChangeStore.getComponentName,
  (newVal) => {
    if (newVal) {
      // Check if there's a tabId in the URL (details or booking tab)
      const urlParams = new URLSearchParams(window.location.search);
      const tabId = urlParams.get("tabId");
      const viewParam = urlParams.get("view");
      
      // Get the current tab ID (from URL or localStorage)
      const currentTabId = tabId || localStorage.getItem("currentTabId") || "main_tab";
      
      // Store component state specific to this tab
      localStorage.setItem(`componentLoad_${currentTabId}`, newVal);
      
      // Also update the generic key for backward compatibility
      localStorage.setItem("componentLoad", newVal);
      
      // If we have a tab ID and view param is different from the new component
      // update the URL to reflect the current view (important for refresh)
      if (tabId && viewParam !== newVal) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('view', newVal);
        window.history.replaceState({}, '', currentUrl.toString());
      }
    }
  }
);
</script>
